import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Divider, Drawer, Popconfirm, Tabs, Typography, message } from 'antd';
import { getVisitReadiness, submitAuditVisit } from '../../services/api';
import { getApiError } from '../../utils/apiError';
import MeetingForm from './MeetingForm';

const { Text } = Typography;

interface Props {
  visitId: string;
  open: boolean;
  openingEditable: boolean;
  meetingEditable: boolean;
  auditorPhase: boolean;
  isLead: boolean;
  onClose: () => void;
  onSubmitted: () => void;
}

const ClosingPanel: React.FC<Props> = ({
  visitId, open, openingEditable, meetingEditable, auditorPhase, isLead, onClose, onSubmitted,
}) => {
  const [problems, setProblems] = useState<string[]>([]);
  const [tab, setTab] = useState('opening');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const refreshReadiness = useCallback(async () => {
    try {
      const res = await getVisitReadiness(visitId);
      setProblems(res.data.problems);
    } catch (err) {
      setError(getApiError(err, 'Could not check readiness.'));
    }
  }, [visitId]);

  useEffect(() => {
    if (open) { setError(''); refreshReadiness(); }
  }, [open, refreshReadiness]);

  const submit = async () => {
    setSubmitting(true);
    setError('');
    try {
      await submitAuditVisit(visitId);
      message.success('Visit submitted to the reviewer.');
      onSubmitted();
    } catch (err) {
      setError(getApiError(err, 'Could not submit the visit.'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Drawer title="Meetings and submission" open={open} onClose={onClose} size={760} destroyOnHidden>
      {error && <Alert type="error" showIcon message={error} style={{ marginBottom: 16 }} />}

      {auditorPhase && !meetingEditable && (
        <Alert
          type="info" showIcon style={{ marginBottom: 16 }}
          message="The meeting records are locked. The reviewer has not asked for the closing meeting to be amended."
        />
      )}

      {auditorPhase && (problems.length === 0 ? (
        <Alert type="success" showIcon message="Everything needed is in place: the visit can go to the reviewer." style={{ marginBottom: 16 }} />
      ) : (
        <Alert
          type="warning" showIcon style={{ marginBottom: 16 }}
          message="Still needed before the visit can be submitted"
          description={<ul style={{ margin: 0, paddingLeft: 18 }}>{problems.map((p) => <li key={p}>{p}</li>)}</ul>}
        />
      ))}

      <Tabs
        activeKey={tab}
        onChange={setTab}
        items={[
          {
            key: 'opening', label: 'Opening meeting',
            children: <MeetingForm visitId={visitId} kind="opening" editable={openingEditable}
              active={open && tab === 'opening'} onChanged={refreshReadiness} />,
          },
          {
            key: 'closing', label: 'Closing meeting',
            children: <MeetingForm visitId={visitId} kind="closing" editable={meetingEditable}
              active={open && tab === 'closing'} onChanged={refreshReadiness} />,
          },
        ]}
      />

      {auditorPhase && isLead && (
        <>
          <Divider />
          <Popconfirm
            title="Submit this visit to the reviewer?"
            description="Answers and NCs lock after submission; only the reviewer can change them."
            okText="Submit" onConfirm={submit} disabled={problems.length > 0}
          >
            <Button type="primary" danger loading={submitting} disabled={problems.length > 0}>
              Submit visit to reviewer
            </Button>
          </Popconfirm>
          {problems.length > 0 && <Text type="secondary" style={{ marginLeft: 12 }}>Available once nothing is pending.</Text>}
        </>
      )}
      {auditorPhase && !isLead && (
        <Text type="secondary" style={{ display: 'block', marginTop: 16 }}>Only the lead auditor can submit the visit.</Text>
      )}
    </Drawer>
  );
};

export default ClosingPanel;
